import cn from 'classnames'
import {
  isValidElement,
  memo,
  MouseEventHandler,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import { renderItemContent } from '../../helpers/renderItemContent'
import { InnerList } from '../innerList/InnerList'
import { ILabelProps } from '../label/models'

import { ICollapseItemProps } from './models'

import styles from './CollapseItem.module.scss'

export const CollapseItem = memo<ICollapseItemProps>(
  ({
    className,
    isVisible = true,
    isDisabled,
    onClick,
    children,
    doExpand,
    hasShadow,
    innerRef,
    keepMounted,
  }) => {
    if (Array.isArray(children) && !isValidElement<ILabelProps>(children[0])) {
      throw 'CollapsedItem component should has Label as first child component'
    }

    if (!Array.isArray(children)) {
      throw 'CollapsedItem component should has Label as first child component and one or some Item components'
    }

    /** --------------------- Variables ------------------------ */

    const [isCollapsed, setIsCollapsed] = useState(true)

    const labelComponent = children[0]
    const list = children.slice(1)
    const isListEmpty = list.length === 0
    const isRendered = !isCollapsed || keepMounted

    const labelItemIcon = useMemo(
      () => (isCollapsed ? 'kbase kbase-omni-chevron-right' : 'kbase kbase-omni-chevron-down'),
      [isCollapsed]
    )

    /** ---------------- Handlers & Callbacks ------------------- */

    const handleOnClick = useCallback<MouseEventHandler<HTMLDivElement>>(
      (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (isDisabled) {
          return
        }

        const nextValue = !isCollapsed

        if (onClick) {
          onClick(nextValue)
        }

        setIsCollapsed(nextValue)
      },
      [isCollapsed, isDisabled, onClick]
    )

    const handleOnMouseDown = useCallback<MouseEventHandler<HTMLDivElement>>((event) => {
      event.preventDefault()
      event.stopPropagation()
    }, [])

    /** ----------------------- Effects ------------------------- */

    useLayoutEffect(() => {
      setIsCollapsed(!doExpand)
    }, [doExpand])

    if (isVisible && !isListEmpty) {
      return (
        <div
          ref={innerRef}
          className={cn(styles.collapseItem, { [styles.disabled]: isDisabled }, className)}
        >
          <div
            role="button"
            onClick={handleOnClick}
            onMouseDown={handleOnMouseDown}
            className={cn(styles.collapseItem_label, {
              [styles.disabled]: isDisabled,
              [styles.expanded]: !isCollapsed,
            })}
          >
            <i className={cn(labelItemIcon, styles.collapseIcon)} />
            {renderItemContent(labelComponent)}
          </div>
          {isRendered && (
            <InnerList
              className={cn({ [styles.collapsed]: isCollapsed, [styles.hasShadow]: hasShadow })}
            >
              {list}
            </InnerList>
          )}
        </div>
      )
    }
    return null
  }
)
